import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Text,
  Alert,
  AlertIcon,
  Heading,
  Link,
  useToast
} from '@chakra-ui/react';
import { signUp } from 'aws-amplify/auth';

export const Signup = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const toast = useToast();

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!email || !password || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);

    try {
      await signUp({
        username: email,
        password: password,
        options: {
          userAttributes: {
            email: email
          }
        }
      });

      toast({
        title: 'Signup successful!',
        description: 'Please check your email for a verification code.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      // Redirect to verification page
      navigate('/verify-email', { state: { email } });

    } catch (error) {
      console.error('Signup error:', error);
      
      let errorMessage = 'An error occurred during signup';
      
      if (error.message.includes('not authorized')) {
        errorMessage = 'This email is not registered for membership. Please contact an administrator to be added as a member first.';
      } else if (error.message.includes('already exists')) {
        errorMessage = 'An account already exists for this email. Please use the login page.';
      } else if (error.message.includes('not available')) {
        errorMessage = 'This account is not available for signup. Please contact an administrator.';
      } else if (error.message.includes('Password')) {
        errorMessage = 'Password must contain uppercase, lowercase, and numbers.';
      } else {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxW="md" py={12}>
      <VStack spacing={8}>
        <Heading size="lg" textAlign="center">
          Create Your Account
        </Heading>
        
        <Text textAlign="center" color="gray.600">
          Sign up with your member email address to access the Golden Gun Club member portal.
        </Text>

        <Box w="100%" as="form" onSubmit={handleSignup}>
          <VStack spacing={4}>
            {error && (
              <Alert status="error">
                <AlertIcon />
                {error}
              </Alert>
            )}

            <FormControl isRequired>
              <FormLabel>Email Address</FormLabel>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your member email address"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Password</FormLabel>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Create a password (min 8 characters)"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Confirm Password</FormLabel>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
              />
            </FormControl>

            <Button
              type="submit"
              colorScheme="blue"
              size="lg"
              w="100%"
              isLoading={loading}
              loadingText="Creating Account..."
            >
              Create Account
            </Button>
          </VStack>
        </Box>

        <Text textAlign="center">
          Already have an account?{' '}
          <Link color="blue.500" onClick={() => navigate('/login')}>
            Sign in here
          </Link>
        </Text>

        <Box textAlign="center" fontSize="sm" color="gray.500">
          <Text>
            Only registered members can create accounts. If you're not yet a member,
            please contact the club administration.
          </Text>
        </Box>
      </VStack>
    </Container>
  );
};